import React, {
  memo, useEffect, useRef,
} from 'react'

import {
  Button, classnames, Icon, RotatingSpinner,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  array, bool, func, node, number, object, oneOf, shape, string,
} from 'prop-types'

import {
  DEFAULT_COMMUNITY_GALLERY_FILTERS,
  iconSize as loadingSpinnerSize,
} from 'Pods/LazyLoaded/CommunityGalleryPanel/constants'

import { NOOP } from 'Utils/stableDefaults'

import Card from './Components/Card/Card'
import useGalleryModels from './Hooks/useGalleryModels'
import MOCK_EXTRACTION_MODELS from './Utils/mockExtractionModels'

const spinnerSize = { height: '32px', width: '32px' }

// First-row cards load eagerly so the above-the-fold content paints fast; the
// rest defer via native lazy loading until they approach the viewport.
const EAGER_COUNT = 15

const filterSignature = ({ searchQuery, category, sortOrder }) => (
  `${searchQuery}|${category}|${sortOrder}`
)

function Gallery({
  searchQuery = DEFAULT_COMMUNITY_GALLERY_FILTERS.searchQuery,
  category = DEFAULT_COMMUNITY_GALLERY_FILTERS.category,
  sortOrder = DEFAULT_COMMUNITY_GALLERY_FILTERS.sortOrder,
  initialModels = null,
  paginationMode = 'infinite',
  onCardClick = NOOP,
  onAuthGatedLoadMore = NOOP,
  anonymousPageLimit = null,
  emptyState = null,
  noMatchNotice = null,
  enableFallback = false,
  extractingCoreStyles = false,
}) {
  const scrollRef = useRef(null)
  const sentinelRef = useRef(null)

  const {
    models,
    hasNextPage,
    isAuthGatedNextPage,
    handleLoadMore,
    isFetchingNextPage,
    isLoading,
    isRefetching,
    isError,
    isShowingFallback,
    activeFilters,
  } = useGalleryModels({
    searchQuery,
    category,
    sortOrder,
    initialModels,
    enableFallback,
    anonymousPageLimit,
  })

  // Reset scroll to the top of the gallery on filter changes. We do this when
  // the filter signature changes (not when new data arrives) so the scroll
  // happens immediately, while keepPreviousData is still showing the dimmed
  // previous grid - matches how filtered listings on commerce sites behave.
  const previousSignatureRef = useRef(filterSignature(activeFilters))
  const signature = filterSignature(activeFilters)
  useEffect(() => {
    if (previousSignatureRef.current === signature) return
    previousSignatureRef.current = signature
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [signature])

  useEffect(() => {
    const root = scrollRef.current
    const sentinel = sentinelRef.current
    const canPaginate = hasNextPage && !isFetchingNextPage
    if (paginationMode !== 'infinite' || !root || !sentinel || !(canPaginate || isAuthGatedNextPage)) {
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        // Anonymous visitors hit the page cap: opening the next page requires
        // signing in, so prompt instead of fetching.
        if (isAuthGatedNextPage) {
          onAuthGatedLoadMore()
        } else if (hasNextPage && !isFetchingNextPage) {
          handleLoadMore()
        }
      },
      { root, rootMargin: '200px', threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [
    paginationMode,
    hasNextPage,
    isAuthGatedNextPage,
    isFetchingNextPage,
    handleLoadMore,
    onAuthGatedLoadMore,
  ])

  // During critical-CSS extraction the page is SSR-rendered synchronously, so the
  // hook only ever resolves to the loading branch and the async states never get
  // their styles captured. Force every state to paint at once here so loading,
  // error, empty, the fallback notice and the populated grid are all in the
  // extracted CSS.
  if (extractingCoreStyles) {
    return (
      <>
        <div className="community-gallery-panel__gallery community-gallery-panel__gallery--loading">
          <Icon svgComponent={RotatingSpinner} size={spinnerSize} />
        </div>
        <div className="community-gallery-panel__gallery community-gallery-panel__gallery--error">
          Failed to load models.
        </div>
        <div className="community-gallery-panel__gallery community-gallery-panel__gallery--empty">
          {emptyState}
        </div>
        <div className="community-gallery-panel__gallery">
          {noMatchNotice}
          <div className="community-gallery-panel__grid">
            {MOCK_EXTRACTION_MODELS.map((model) => (
              <Card
                key={model.id}
                id={model.id}
                title={model.title}
                prompt={model.prompt}
                createdAt={model.createdAt}
                image={model.image}
                authorAvatar={model.authorAvatar}
                eager
                onRemix={onCardClick}
              />
            ))}
          </div>
          <div className="community-gallery-panel__load-more">
            <Button type="secondary" size="small" onClick={handleLoadMore}>
              Load more
            </Button>
          </div>
        </div>
      </>
    )
  }

  if (isLoading) {
    return (
      <div className="community-gallery-panel__gallery community-gallery-panel__gallery--loading">
        <Icon svgComponent={RotatingSpinner} size={spinnerSize} />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="community-gallery-panel__gallery community-gallery-panel__gallery--error">
        Failed to load models.
      </div>
    )
  }

  // `keepPreviousData` keeps prior results in `models` while a filter-driven
  // refetch is in flight, so reaching this branch means the request resolved
  // with zero items - genuine empty rather than mid-transition. On hosts with a
  // fallback enabled, the hook substitutes popular models here, so this only
  // hits when even the fallback is empty.
  if (models.length === 0) {
    return (
      <div className="community-gallery-panel__gallery community-gallery-panel__gallery--empty">
        {emptyState}
      </div>
    )
  }

  return (
    <div className="community-gallery-panel__gallery" ref={scrollRef}>
      {isShowingFallback && noMatchNotice}
      <div
        className={classnames(
          'community-gallery-panel__grid',
          isRefetching && 'community-gallery-panel__grid--refetching',
        )}
        aria-busy={isRefetching || undefined}
      >
        {models.map((model, index) => (
          <Card
            key={model.id}
            id={model.id}
            slug={model.slug}
            title={model.title}
            prompt={model.prompt}
            createdAt={model.createdAt}
            image={model.image}
            likeCount={model.likeCount}
            commentsCount={model.commentsCount}
            eager={index < EAGER_COUNT}
            onRemix={onCardClick}
          />
        ))}
      </div>
      {(hasNextPage || isAuthGatedNextPage) && paginationMode === 'loadMore' && (
        <div className="community-gallery-panel__load-more">
          <Button
            type="secondary"
            size="small"
            onClick={isAuthGatedNextPage ? onAuthGatedLoadMore : handleLoadMore}
            disabled={isFetchingNextPage}
            icon={isFetchingNextPage
              ? <Icon svgComponent={RotatingSpinner} size={loadingSpinnerSize} />
              : null}
          >
            Load more
          </Button>
        </div>
      )}
      {(hasNextPage || isAuthGatedNextPage) && paginationMode === 'infinite' && (
        <div
          ref={sentinelRef}
          className="community-gallery-panel__sentinel"
          aria-hidden="true"
        />
      )}
    </div>
  )
}

Gallery.propTypes = {
  searchQuery: string,
  category: string,
  sortOrder: string,
  initialModels: shape({
    data: array,
    meta: object,
  }),
  paginationMode: oneOf(['infinite', 'loadMore']),
  onCardClick: func,
  onAuthGatedLoadMore: func,
  anonymousPageLimit: number,
  emptyState: node,
  noMatchNotice: node,
  enableFallback: bool,
  extractingCoreStyles: bool,
}

export default memo(Gallery)
